import { ReactElement,  useEffect, useState } from "react";
import {
  Box,
  // Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
// import IconButton from "@mui/material/IconButton";
// import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { IAddition } from "../../../../types";
import { formatDateTimeWithSeconds } from "../../../../system/utils";
import TableContainer  from "../../../../components/tableContainer";
import RadarChart from "../../../../components/charts/radarChart";
import { getPalette } from "../../../../configuration/configuration";
import { IAxisBand, IAxisLinear, IPoint as CommonPoint, IRadarChartConfig } from "../../../../components/charts/types";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";


interface IAdditionPanelProps {
  sourceType: number;
  additions: IAddition[];
}

const Row = (props: { row: IAddition, }) => {
  const { row } = props;
  // const [open, setOpen] = useState(false);
  const [ radarConfig, setRadarConfig ] = useState<IRadarChartConfig | null>(null);

  useEffect(() => {
    const items: CommonPoint[] = [];

    const yAxis: IAxisLinear = {
      name: "yAxis",
      minimum: 0,
      maximum: 100,
      ticksCount: 4,
      hiddenTickText: true,
      formatTick: (n) => (n as number).toFixed(0)
    };

    const xAxis: IAxisBand = {
      name: "xAxis",
      categories: row.additionDetail.map(x => x.material.name)
    };

    row.additionDetail.forEach(r => {

      items.push({
        x: r.material.name,
        y: Math.abs(((r.suggestedValue ?? 0) - r.weight) * 100 / (r.suggestedValue ?? 1))
      });
    });

    setRadarConfig({
      width: 250,
      height: 250,
      title: "Gap",
      showLegend: true,
      series: [{
        name: "Alloys",
        xAxisName: "yAxis",
        yAxisName: "xAxis",
        value: items,
        color: getPalette().list[5],
        unit: "s",
        ranges: [
          {
            minimum: 0,
            maximum: 10,
            color: getPalette().statusColor.good.background },
          {
            minimum: 10,
            maximum: 20,
            color: getPalette().statusColor.warning.background },
          {
            minimum: 20,
            maximum: 500,
            color: getPalette().statusColor.error.background }]
      }],
      yAxis,
      xAxis
    });
  }, []);


  return (
    <>
      <TableRow style={{ width: "100%" }} hover={true}>
        <TableCell>
          {/*<IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>*/}
          {/*  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}*/}
          {/*</IconButton>*/}
        </TableCell>
        <TableCell component="th" scope="row">
          {row.source.name}
        </TableCell>
        <TableCell>{formatDateTimeWithSeconds(row.beginDatetime)}</TableCell>
        <TableCell>{formatDateTimeWithSeconds(row.endDatetime)}</TableCell>
        <TableCell align="right">{row.weight.toFixed(1)}</TableCell>
        <TableCell />
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          {/*<Collapse in={open} timeout="auto" unmountOnExit={true}>*/}
          <Box sx={{ m: 1 }}>
            <Typography variant="h6" gutterBottom={true} component="div">
              Detail
            </Typography>
            <Table size="small" stickyHeader={true} aria-label="purchases">
              <TableHead>
                <TableRow>
                  <TableCell width={120}>Material</TableCell>
                  <TableCell align="right" width={120}>Suggested (lb)</TableCell>
                  {/*<TableCell align="right" width={120}>Set Point (lb)</TableCell>*/}
                  <TableCell align="right" width={120}>Weight (lb)</TableCell>
                  <TableCell align="right" width={100}>%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.additionDetail.map((detail) => {
                  const tolerance = 5;
                  const diff = detail.suggestedValue
                    ? (detail.weight - detail.suggestedValue) * 100 / detail.suggestedValue
                    : 0;

                  return (
                    <TableRow key={detail.heatAdditionDetailID} hover={true}>
                      <TableCell component="th" scope="row">
                        {detail.material.name}
                      </TableCell>
                      <TableCell align="right">{(detail.suggestedValue ?? 0).toFixed(1)}</TableCell>
                      {/*<TableCell align="right">{(detail.setPointValue ?? 0).toFixed(1)}</TableCell>*/}
                      <TableCell align="right">
                        { detail.weight.toFixed(1) }
                      </TableCell>
                      <TableCell align="right">
                        { diff.toFixed(2) }
                        { diff > tolerance &&
                          <ArrowUpwardIcon className="ml-1" fontSize="small"
                            style={{ color: "#D32F2F", verticalAlign: "middle" }} />
                        }
                        { diff < -tolerance &&
                          <ArrowDownwardIcon className="ml-1" fontSize="small"
                            style={{ color: "#F57C00", verticalAlign: "middle"  }} />
                        }
                        { Math.abs(diff) <= tolerance &&
                          <VerticalAlignCenterIcon className="ml-1" fontSize="small"
                            style={{ verticalAlign: "middle"  }} />
                        }
                      </TableCell>
                    </TableRow>);
                })}
              </TableBody>
            </Table>
          </Box>
          {/*</Collapse>*/}
        </TableCell>
        <TableCell>
          {radarConfig && <RadarChart radarConfig={radarConfig} selectedItem={null}/>}
        </TableCell>
      </TableRow>
    </>
  );
};


export const AdditionPanel = ({ sourceType, additions }: IAdditionPanelProps): ReactElement => {

  return(
    <TableContainer style={{ maxHeight: 400 }} className="flex-grow-1">
      <Table stickyHeader={true} size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell style={{ backgroundColor: "#b0bec5" }} width={30}/>
            <TableCell style={{ backgroundColor: "#b0bec5" }} width={200}>Source Name</TableCell>
            <TableCell style={{ backgroundColor: "#b0bec5" }} width={180}>Begin</TableCell>
            <TableCell style={{ backgroundColor: "#b0bec5" }} width={180}>End</TableCell>
            <TableCell style={{ backgroundColor: "#b0bec5" }} width={160} align="right" >Total Weight (lb)</TableCell>
            <TableCell style={{ backgroundColor: "#b0bec5" }} />
          </TableRow>
        </TableHead>
        <TableBody>
          { additions.filter(x => x.source.sourceType === sourceType).map((row) =>
            <Row key={`addition-row-${row.heatAdditionID}`} row={row} />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
